import React, {MouseEvent, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TShow} from '../../../../hooks/useShow';
import {TEticketSeat, TEticketSeatMap, TEticketSeatPos} from '../../../../redux/eTicket/eTicket.type';
import SheetShell from '../SheetShell/SheetShell';
import TicketSeatTiles from '../TicketSeatTiles/TicketSeatTiles';
import SeatGrid from '../SeatGrid/SeatGrid';
import VenueLine from '../VenueLine/VenueLine';
import sanitizeHtml from '../../../../library/sanitizeHtml';
import {generateSVG, getAreaBoundingBox} from '../../../../functions/generateSVG';
import * as css from './SeatMapSheet.css';

interface SeatMapSheetProps {
    show: TShow;
    venue?: string;
    seat?: TEticketSeat;
    design?: TEticketSeatMap;
}

// Where THIS ticket's seat sits on the overview map: its normalized position inside the block's
// seat grid, projected onto the block path's bounding box. smap geometry preserves real-world
// proportions (seat coords = world px / pitch, hull from the same points), so the pin lands
// within about half a seat pitch; if the seat can't be matched it falls back to the block center.
const getSeatMarker = (seats: TEticketSeatPos[], area: object, seat?: TEticketSeat) => {
    const box = getAreaBoundingBox(area);
    if(!box) {
        return undefined;
    }
    const mine = seat && seats.find(s => String(s.seat) === String(seat.seat) && String(s.row) === String(seat.row));
    const minX = Math.min(...seats.map(s => s.x));
    const minY = Math.min(...seats.map(s => s.y));
    const spanX = Math.max(...seats.map(s => s.x)) - minX;
    const spanY = Math.max(...seats.map(s => s.y)) - minY;
    const nx = mine && spanX ? (mine.x - minX) / spanX : 0.5;
    const ny = mine && spanY ? (mine.y - minY) / spanY : 0.5;
    return {x: box.x + nx * box.width, y: box.y + ny * box.height};
};

function SeatMapSheet({show, venue, seat, design}: SeatMapSheetProps): React.ReactElement {
    const {t} = useTranslation('eTicket');
    const [zoomedAid, setZoomedAid] = useState<string>();
    const myAid = seat?.aid != null ? String(seat.aid) : undefined;

    // The sheet stays mounted while closed, so reset back to the block overview on close —
    // otherwise re-opening jumps straight to the seat grid.
    if(!show.isOpen && zoomedAid) {
        setZoomedAid(undefined);
    }

    // Highlight only the block of THIS ticket (not every block bought in the order),
    // and pin the ticket's own seat inside it.
    const svg = useMemo(() => {
        if(!design) {
            return null;
        }
        const area = design.area && myAid
            ? Object.fromEntries(Object.entries(design.area).map(([aid, item]) => [aid, {...item, mine: aid === myAid}]))
            : design.area;
        const myArea = myAid ? design.area?.[myAid] : undefined;
        const marker = myArea?.seats?.length ? getSeatMarker(myArea.seats, myArea, seat) : undefined;
        return generateSVG({...design, area, marker});
    }, [design, myAid, seat]);

    const zoomedSeats = zoomedAid ? design?.area?.[zoomedAid]?.seats : undefined;

    const onMapClick = (event: MouseEvent<HTMLDivElement>) => {
        const aid = (event.target as Element).closest('[data-mine]')?.getAttribute('data-aid');
        if(aid && design?.area?.[aid]?.seats?.length) {
            setZoomedAid(aid);
        }
    };

    return (
        <SheetShell show={show} title={t('seatMapTitle')} data-cy="seat-map-sheet">
            <css.body>
                {zoomedSeats?.length ? (
                    <SeatGrid seats={zoomedSeats} mySeat={seat} onBack={() => setZoomedAid(undefined)} />
                ) : (
                    <>
                        <VenueLine name={venue} />
                        <TicketSeatTiles seat={seat} />
                        {svg && (
                            // re-keyed per open so the pin's drop-in replays (the sheet stays mounted while closed)
                            <css.map key={String(show.isOpen)} data-cy="seat-map-svg" onClick={onMapClick} dangerouslySetInnerHTML={sanitizeHtml(svg)} />
                        )}
                    </>
                )}
            </css.body>
        </SheetShell>
    );
}

export default SeatMapSheet;
